/**
 * src/components/spatial/SpatialObjectPointLayer.tsx
 *
 * Fallback marker layer for spatial objects whose geometry is still a
 * placeholder Point. Mapbox/MapLibre fill-extrusion layers only paint
 * Polygon and MultiPolygon, so a Point feature would otherwise be
 * silently invisible — operators see "1 feature in viewport" in the
 * console but nothing on the map and assume rendering is broken.
 *
 * This layer renders every Point feature in the shared spatial-objects
 * source as a coloured circle reading from the same canonical
 * `colorKey` / `fill_color` aliases as the extrusion layers, so the
 * operator can see, click, and fly to placeholder objects until they
 * upgrade the geometry to a real footprint.
 */

import React from 'react';
import type { SpatialMapProvider } from '../../services/spatial/mapProvider';
import {
  SPATIAL_LAYER_IDS,
  SPATIAL_SOURCE_ID,
  buildExtrusionColorExpr,
} from './spatialLayerUtils';
import { useStrictModeSafeLayer } from './useStrictModeSafeLayer';

export interface SpatialObjectPointLayerProps {
  provider: SpatialMapProvider;
  sourceId?: string;
  layerId?: string;
  beforeId?: string;
}

const SpatialObjectPointLayer: React.FC<SpatialObjectPointLayerProps> = ({
  provider,
  sourceId = SPATIAL_SOURCE_ID,
  layerId  = SPATIAL_LAYER_IDS.point,
  beforeId,
}) => {
  useStrictModeSafeLayer({
    provider,
    layerId,
    beforeId,
    buildSpec: () => ({
      id:     layerId,
      type:   'circle',
      source: sourceId,
      // Only render Point geometries — Polygon / MultiPolygon are
      // handled by the extrusion layers (building / floor / zone /
      // slice).
      filter: ['==', ['geometry-type'], 'Point'] as unknown[],
      paint: {
        // Reuse the same `colorKey` / `fill_color` lookup as the
        // building extrusion so a custom colour applies on both
        // representations.
        'circle-color':         buildExtrusionColorExpr('building'),
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          0,  3,
          10, 5,
          16, 9,
          20, 14,
        ],
        'circle-stroke-color':  '#ffffff',
        'circle-stroke-width':  1.5,
        'circle-opacity':       0.9,
        'circle-stroke-opacity': 0.85,
      },
    }),
  });

  return null;
};

export default SpatialObjectPointLayer;
